import React, { useState, useCallback, useEffect } from 'react';
import { format } from 'date-fns';
import TableComponent from '../../../components/Table/TableComponent';
import SearchIcon from '@mui/icons-material/Search';
import { TableRow, TableCell } from '@mui/material';
import { PatientHistory } from '../../../components/interface/SubjectiveInterfaces/SurgicalHistoryInterface';
import { useAuth } from '../../../context/AuthContext';
import DateRangePicker from '../../../components/FormFilelds/DateRangePicker';

import TablePaginationComponent from '../../../components/TablePaginationComponent';
import { fetchSurgicalHistory } from '../../../services/SubjectiveServices/SurgicalHistoryformService';
import IconButton, { ClearButton } from '../../../components/Buttons/SubjectiveIcons';
import SnackbarMessage from '../../../components/SnackBAr/SnackbarNotification';
import LoadingAnimation from '../../../components/LoadingAnimation';




interface SurgicalHistoryFormProps {
    patientId: string;
}

const SurgicalHistoryForm: React.FC<SurgicalHistoryFormProps> = ({ patientId }) => {
    const { token } = useAuth();
    const [surgicalHistories, setSurgicalHistories] = useState<PatientHistory[]>([]);
    const [totalSurgicalHistories, setTotalSurgicalHistories] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    
   
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
   
    const [dateRange, setDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);
    


    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'info' | 'warning' | 'error'>('success');


    const [showClearFilterButton, setShowClearFilterButton] = useState(false);

    const showSnackbar = (message: string, severity: 'success' | 'info' | 'warning' | 'error') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const fetchSurgicalHistoriesData = useCallback(async (initial = false) => {
        setLoading(true);
        setError('');

        try {
            // Add a delay of 1 second
            await new Promise(resolve => setTimeout(resolve, 300));

            const params: Record<string, any> = {
                limit: rowsPerPage,
                page: page + 1,
                order_by: 'values->surgery_date',
                dir: 2,
                user_id: patientId,
                slug: 'surgical-history',
                searchkey: searchTerm,
                slug_array: ''
            };

            if (!initial) {
                params.from = dateRange.start;
                params.to = dateRange.end;
            }

            const data = await fetchSurgicalHistory(token!, params);
            console.log("data", data);

            const fetchedHistories = data.data.patient_histories.map((item: { id: any; consult_id: any; freeze: any; values: { surgery_date: any; surgery_notes: any; surgical_name: { id: any; name: any; slug: any; is_active: any; attributes: { cpt_code: any; }; master_type_slug: any; }; surgery_status: any; surgery_done_by: any; pre_surgery_diagnosis: { id: any; name: any; slug: any; is_active: any; attributes: string; master_type_slug: any; }; }; }) => ({
                id: item.id,
                patient_id: patientId,
                consult_id: item.consult_id || null,
                slug: 'surgical-history',
                freeze: item.freeze,
                values: {
                    surgery_date: item.values.surgery_date,
                    surgery_notes: item.values.surgery_notes,
                    surgical_name: {
                        id: item.values.surgical_name.id,
                        name: item.values.surgical_name.name,
                        slug: item.values.surgical_name.slug,
                        is_active: item.values.surgical_name.is_active,
                        attributes: {
                            cpt_code: item.values.surgical_name?.attributes?.cpt_code
                        },
                        master_type_slug: item.values.surgical_name.master_type_slug
                    },
                    surgery_status: item.values.surgery_status,
                    surgery_done_by: item.values.surgery_done_by,
                    pre_surgery_diagnosis: {
                        id: item.values.pre_surgery_diagnosis.id,
                        name: item.values.pre_surgery_diagnosis.name,
                        slug: item.values.pre_surgery_diagnosis.slug,
                        is_active: item.values.pre_surgery_diagnosis.is_active,
                        attributes: {
                            link: item.values.pre_surgery_diagnosis?.attributes?.link
                        },
                        master_type_slug: item.values.pre_surgery_diagnosis.master_type_slug
                    }
                }
            }));

            console.log(fetchedHistories);
            setSurgicalHistories(fetchedHistories);
            setTotalSurgicalHistories(data.data.pagination.total || 0);

            setShowClearFilterButton(!initial || searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== ''));

        } catch (error) {
            setError('Failed to fetch surgical histories.');
            console.error('Error fetching surgical histories', error);
            showSnackbar('Failed to fetch surgical histories.', 'error');
        } finally {
            setLoading(false);
        }
    }, [searchTerm, dateRange, page, rowsPerPage, patientId]);


    useEffect(() => {
        fetchSurgicalHistoriesData(true);
    }, [fetchSurgicalHistoriesData]);

    useEffect(() => {
        const isSearchable = searchTerm.trim() !== '' || (dateRange.start !== '' && dateRange.end !== '');
        setIsButtonEnabled(isSearchable);
    }, [searchTerm, dateRange]);

    const handleSearch = useCallback(() => {
        if (dateRange.start && dateRange.end) {
            setPage(0);
            setLoading(true);
            fetchSurgicalHistoriesData().finally(() => setLoading(false));
        } else {
            console.warn('Please select both from date and to date.');
            showSnackbar('Please select both from date and to date.', 'warning');
        }
    }, [fetchSurgicalHistoriesData, dateRange]);

    const handleButtonClick = () => {
        handleSearch();

    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setDateRange({ start: '', end: '' });
        setPage(0);
        fetchSurgicalHistoriesData(true);
    };


    const formatDate = (dateString: string | number | Date) => {
      try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) {
              return 'Invalid Date'; // Or return an empty string, "N/A", etc.
          }
          return format(date, 'dd-MM-yyyy');
      } catch (error) {
          console.error("Error formatting date:", error);
          return 'Invalid Date'; // Handle invalid date values
      }
  };

    const renderRow = (item: PatientHistory, index: number) => (
        <TableRow key={item.id || index}>
            <TableCell className="px-4 py-3 font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}> {page * rowsPerPage + index + 1}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{formatDate(item.values.surgery_date)}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{item.values.surgical_name.name}</TableCell>
            <TableCell className="font-jost" sx={{ fontFamily: 'Jost, sans-serif' , fontSize: '1rem'  }}>{item.values.surgery_done_by}</TableCell>
        </TableRow>
    );

    return (
        <div>
            <div className="flex flex-wrap items-center gap-4 w-full pb-4">
                <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                    <DateRangePicker
                        startDateId="surgicalhistory_Start_Date"
                        endDateId="surgicalhistory_End_Date"
                        value={{ startDate: dateRange.start, endDate: dateRange.end }}
                        onChange={dates => {
                            setDateRange({ start: dates.startDate || '', end: dates.endDate || '' });
                        }}
                    />
                    <div className="flex items-center gap-2 shrink-0">
                        <IconButton
                            onClick={handleButtonClick}
                            disabled={!isButtonEnabled}
                            icon={<SearchIcon />}
                        />
                        {showClearFilterButton && (
                            <ClearButton onClick={handleClearFilters} />
                        )}
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center my-4"><LoadingAnimation /></div>
            ) : (
                <div>
                    <TableComponent
                        headers={['S.No', 'Onset Date', 'Surgery Name', 'Done By']}
                        data={surgicalHistories}
                        loading={loading}
                        error={error}
                        renderRow={renderRow}
                    />
                   
                </div>
            )}
            <TablePaginationComponent
                count={totalSurgicalHistories}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_event, newPage) => {
                    setPage(newPage);
                }}
                onRowsPerPageChange={(event) => {
                    const newRowsPerPage = parseInt(event.target.value, 10);
                    setRowsPerPage(newRowsPerPage);
                    setPage(0);
                }}
            />
          {/* Snackbar Component */}
          <SnackbarMessage
                    open={snackbarOpen}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={() => setSnackbarOpen(false)}
                />
        </div>
    );
};

export default SurgicalHistoryForm;

